import React, { useState, useEffect, useCallback } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { useClient, useUpdateUser } from 'shared/apollo';

import Auth from 'shared/auth';
import AccountEditor from 'shared/no-auth/components/AccountEditor/AccountEditor';
import { Header, ButtonBar, Button, Body } from './styled';
import ImagePicker, { ImageUploadButton } from 'shared/components/ImagePicker';
import { Field } from 'shared/containers/form';
import useAccount from '../Organization/useAccount';
import {
  logoMutation,
  deleteUserLogoMutation,
} from '../Organization/mutations';
import { useUserActions } from 'shared/auth/hooks/useUserActions';

export default function MyAccount() {
  const client = useClient();
  const { run: runUpdate } = useUpdateUser();
  const {
    userAttributes,
    handleSubmit: userSubmit,
    handleEmailVerified,
  } = useUserAttributes();
  const account = useAccount();
  const [pwError, setPwError] = useState();
  const [pwCheck, setPwCheck] = useState();

  const handleChangePassword = async (
    oldPassword,
    newPassword,
    confirmPassword,
  ) => {
    Auth.currentAuthenticatedUser()
      .then(user => {
        if (newPassword === confirmPassword) {
          return Auth.changePassword(user, oldPassword, newPassword);
        }
        setPwCheck('New and Confirm Passwords do not Match');
      })
      .then(data => { /* noop */ })
      .catch(err => {
        setPwError(err?.message);
      });
  };

  const [fileToUpload, setFileToUpload] = useState(null);
  const [initialLogo, setInitialLogo] = useState(null);
  const [newLogoUrl, setNewLogoUrl] = useState();

  useEffect(() => {
    if (account?.userLogo) {
      setInitialLogo(account?.userLogo);
    }
  }, [account]);

  const { name } = useUserActions(); // TODO: this needs to be returned from backend

  const handleFile = file => {
    setFileToUpload(file);
  };

  const handleUpload = useCallback(({ target: { value } }) => {
    const { url, file } = value;
    setNewLogoUrl(url);
  }, []);

  const handleDeleteLogo = async () => {
    setNewLogoUrl(null);
    setInitialLogo(null);
    await client.mutate({
      mutation: deleteUserLogoMutation,
      variables: { id: account.userId },
    });
  };

  const handleSubmit = async values => {
    setPwError();
    setPwCheck();
    if (
      values.old_password &&
      values.new_password &&
      values.old_password.length >= 1 &&
      values.new_password.length >= 1
    ) {
      handleChangePassword(
        values.old_password,
        values.new_password,
        values.confirm_password,
      );
    }
    if (fileToUpload) {
      const logo = await fileToUpload();
      if (logo) {
        await client.mutate({
          mutation: logoMutation,
          variables: {
            type: 'user_logo',
            logo,
          },
        });
      }
    }
    await runUpdate({
      id: account.userId,
      firstName: values.given_name,
      lastName: values.family_name,
    });
    userSubmit(values);
  };

  const header = (
    <Header>
      <h2>My Account</h2>
      <ButtonBar>
        <ImagePicker
          value={newLogoUrl ?? initialLogo}
          forceDirty={!!newLogoUrl}
          onFile={handleFile}
          size={50}
          style={{ display: 'flex' }}
        >
          <b>{name}</b>
          <Field
            name="fileToUpload"
            onChange={handleUpload}
            component={ImageUploadButton}
          >
            <Button component="span">Upload New Picture</Button>
          </Field>
        </ImagePicker>
        <Button variant="outlined" onClick={handleDeleteLogo}>
          Delete Picture
        </Button>
      </ButtonBar>
    </Header>
  );

  if (!userAttributes) {
    return <LinearProgress variant="query" />;
  }

  return (
    <AccountEditor
      submitButtonTitle="Save All Changes"
      submitFullWidth
      mode="edit"
      onChangePassword={handleChangePassword}
      {...{ userAttributes, handleSubmit, handleEmailVerified, header }}
      BodyComponent={Body}
      pwError={pwError}
      pwCheck={pwCheck}
    />
  );
}

export function useUserAttributes() {
  const [userAttributes, setUserAttributes] = useState();
  useEffect(() => {
    let active = true;
    (async () => {
      const info = await Auth.currentUserInfo();
      if (!info) {
        return;
      }
      const { attributes } = info;
      setUserAttributes(attributes);
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleSubmit = useCallback(
    async values => {
      try {
        delete values['old_password'];
        delete values['new_password'];
        delete values['confirm_password'];
        const user = await Auth.currentAuthenticatedUser();
        const shouldCheckEmail = values.email !== userAttributes.email;
        if (shouldCheckEmail) {
          values.email_verified = false;
        }
        if (values['custom:title'] === '#trueimpactadmin#') {
          delete values['custom:title'];
          values['custom:account_id'] = '1';
        }
        await Auth.updateUserAttributes(user, values);
        setUserAttributes({
          ...userAttributes,
          ...values,
        });

        if (shouldCheckEmail) {
          alert(
            `Please check you inbox at ${values.email} for a verification code!`,
          );
        }
      } catch (err) {
        alert(err.message || err.errorMessage || err);
      }
    },
    [userAttributes, setUserAttributes],
  );
  const handleEmailVerified = useCallback(() => {
    setUserAttributes(oldAttributes => ({
      ...oldAttributes,
      email_verified: true,
    }));
  }, [setUserAttributes]);

  return { userAttributes, handleSubmit, handleEmailVerified };
}
